import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Divider,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalanceWallet as WalletIcon,
  Person as PersonIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { walletAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const RechargeManagement = () => {
  const { user } = useAuth();
  const [recharges, setRecharges] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    user_id: '',
    date_from: '',
    date_to: '',
    limit: 50,
    offset: 0,
  });

  // Detail dialog
  const [selectedRecharge, setSelectedRecharge] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  useEffect(() => {
    fetchRechargeHistory();
  }, [filters]);

  const fetchRechargeHistory = async () => {
    try {
      setLoading(true);
      const response = await walletAPI.getRechargeHistory(filters);
      if (response.data.success) {
        setRecharges(response.data.recharges);
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching recharge history:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch recharge history');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (transactionId) => {
    if (!window.confirm('Are you sure you want to approve this recharge?')) {
      return;
    }

    try {
      setActionLoading(true);
      const response = await walletAPI.approveRecharge(transactionId, 'approve');
      
      if (response.data.success) {
        toast.success(response.data.message || 'Recharge approved successfully!');
        fetchRechargeHistory();
      }
    } catch (error) {
      console.error('Error approving recharge:', error);
      toast.error(error.response?.data?.message || 'Failed to approve recharge');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (transactionId) => {
    if (!window.confirm('Are you sure you want to reject this recharge?')) {
      return;
    }

    try {
      setActionLoading(true);
      const response = await walletAPI.approveRecharge(transactionId, 'reject');
      
      if (response.data.success) {
        toast.success(response.data.message || 'Recharge rejected successfully!');
        fetchRechargeHistory();
      }
    } catch (error) {
      console.error('Error rejecting recharge:', error);
      toast.error(error.response?.data?.message || 'Failed to reject recharge');
    } finally {
      setActionLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      offset: 0 // Reset pagination when filters change
    }));
  };

  const handleViewDetails = (recharge) => {
    setSelectedRecharge(recharge);
    setDetailDialogOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <Box sx={{ p: 3 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
          <ReceiptIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Recharge Management
        </Typography>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Total Recharges</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {stats.total || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Pending</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {stats.pending || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Approved</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {stats.approved || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Total Amount</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {formatCurrency(stats.total_amount || 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="From Date"
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => handleFilterChange('date_from', e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="To Date"
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => handleFilterChange('date_to', e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={fetchRechargeHistory}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Recharges Table */}
        <Card>
          <CardContent>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <CircularProgress />
              </Box>
            ) : recharges.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <Typography variant="h6" color="textSecondary">
                  No recharge requests found
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Payment Method</TableCell>
                      <TableCell>Reference ID</TableCell>
                      <TableCell>Screenshot</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recharges.map((recharge) => (
                      <TableRow key={recharge.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            #{recharge.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <PersonIcon fontSize="small" color="primary" />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {recharge.toUser?.username}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {recharge.toUser?.user_type}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                            {formatCurrency(recharge.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {recharge.payment_method || 'UPI'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {recharge.reference_id || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {recharge.bank_info?.screenshot_url ? (
                            <Tooltip title="Click to view screenshot">
                              <Box
                                component="a"
                                href={recharge.bank_info.screenshot_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  display: 'inline-block',
                                  cursor: 'pointer',
                                  '&:hover': { opacity: 0.8 }
                                }}
                              >
                                <Box
                                  component="img"
                                  src={recharge.bank_info.screenshot_url}
                                  alt="Payment Proof"
                                  sx={{
                                    width: 50,
                                    height: 50,
                                    objectFit: 'cover',
                                    borderRadius: 1,
                                    border: '1px solid #ddd'
                                  }}
                                />
                              </Box>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="textSecondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={recharge.status.toUpperCase()}
                            color={getStatusColor(recharge.status)}
                            size="small"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(recharge.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            {recharge.status === 'pending' && (
                              <>
                                <Tooltip title="Approve">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleApprove(recharge.id)}
                                    disabled={actionLoading}
                                  >
                                    <ApproveIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Reject">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleReject(recharge.id)}
                                    disabled={actionLoading}
                                  >
                                    <RejectIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleViewDetails(recharge)}
                              >
                                <ReceiptIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Detail Dialog */}
        <Dialog
          open={detailDialogOpen}
          onClose={() => setDetailDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Recharge Details
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            {selectedRecharge && (
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="textSecondary">Transaction ID</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    #{selectedRecharge.id}
                  </Typography>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="caption" color="textSecondary">User</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {selectedRecharge.toUser?.username} ({selectedRecharge.toUser?.user_type})
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="textSecondary">Amount</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {formatCurrency(selectedRecharge.amount)}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="textSecondary">Payment Method</Typography>
                  <Typography variant="body1">
                    {selectedRecharge.payment_method || 'UPI'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="textSecondary">Reference ID</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {selectedRecharge.reference_id || '-'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="textSecondary">Status</Typography>
                  <Chip
                    label={selectedRecharge.status.toUpperCase()}
                    color={getStatusColor(selectedRecharge.status)}
                    size="small"
                    sx={{ mt: 0.5, fontWeight: 'bold' }}
                  />
                </Box>

                <Divider />

                <Box>
                  <Typography variant="caption" color="textSecondary">Created At</Typography>
                  <Typography variant="body2">
                    {formatDate(selectedRecharge.created_at)}
                  </Typography>
                </Box>

                {selectedRecharge.approved_at && (
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      {selectedRecharge.status === 'approved' ? 'Approved At' : 'Rejected At'}
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(selectedRecharge.approved_at)}
                    </Typography>
                  </Box>
                )}

                {selectedRecharge.notes && (
                  <Box>
                    <Typography variant="caption" color="textSecondary">Notes</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedRecharge.notes}
                    </Typography>
                  </Box>
                )}

                {selectedRecharge.bank_info?.screenshot_url && (
                  <Box>
                    <Typography variant="caption" color="textSecondary">Payment Screenshot</Typography>
                    <Box
                      component="a"
                      href={selectedRecharge.bank_info.screenshot_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: 'block', mt: 1 }}
                    >
                      <Box
                        component="img"
                        src={selectedRecharge.bank_info.screenshot_url}
                        alt="Payment Proof"
                        sx={{
                          maxWidth: '100%',
                          maxHeight: 300,
                          objectFit: 'contain',
                          borderRadius: 2,
                          border: '1px solid #ddd',
                          cursor: 'pointer',
                          '&:hover': { opacity: 0.9 }
                        }}
                      />
                    </Box>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                      Click image to open in new tab
                    </Typography>
                  </Box>
                )}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            {selectedRecharge?.status === 'pending' && (
              <>
                <Button
                  onClick={() => {
                    handleApprove(selectedRecharge.id);
                    setDetailDialogOpen(false);
                  }}
                  color="success"
                  variant="contained"
                  startIcon={<ApproveIcon />}
                  disabled={actionLoading}
                >
                  Approve
                </Button>
                <Button
                  onClick={() => {
                    handleReject(selectedRecharge.id);
                    setDetailDialogOpen(false);
                  }}
                  color="error"
                  variant="outlined"
                  startIcon={<RejectIcon />}
                  disabled={actionLoading}
                >
                  Reject
                </Button>
              </>
            )}
            <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </motion.div>
    </Box>
  );
};

export default RechargeManagement;
